import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { CITY_PAGES } from "@/lib/citiesData";
import { INDUSTRY_PAGES } from "@/lib/industriesData";
import { ARTICLES, TOPIC_LANDINGS } from "@/lib/learningHub";
import { INTERSECTION_CONFIGS } from "@/lib/intersectionData";
import { buildBreadcrumbScript } from "@/lib/serviceHubSchema";

export const Route = createFileRoute("/sitemap")({
  head: () => ({
    meta: [
      { title: "Site Map — Every Page on Colorado Web Impressions" },
      {
        name: "description",
        content:
          "Browse every page on Colorado Web Impressions: services, pricing, city pages, industries, learning hub articles, legal, and machine-readable feeds.",
      },
      { property: "og:title", content: "Site Map — Colorado Web Impressions" },
      {
        property: "og:description",
        content:
          "A human-friendly index of every major URL on the site, plus downloadable XML and CSV sitemaps.",
      },
    
      { name: "robots", content: "noindex,nofollow" },
    ],
    links: [
      { rel: "canonical", href: "https://coloradowebimpressions.com/sitemap" },
    ],
    scripts: [
      buildBreadcrumbScript([
        { name: "Home", path: "/" },
        { name: "Site Map", path: "/sitemap" },
      ]),
    ],
  }),
  component: SitemapPage,
});

type LinkRow = { label: string; to: string };

function Section({
  title,
  description,
  links,
  query,
}: {
  title: string;
  description?: string;
  links: LinkRow[];
  query: string;
}) {
  const q = query.trim().toLowerCase();
  const filtered = q
    ? links.filter(
        (l) =>
          l.label.toLowerCase().includes(q) || l.to.toLowerCase().includes(q),
      )
    : links;

  if (filtered.length === 0) return null;

  return (
    <section className="rounded-2xl border border-border bg-card p-6 shadow-card lg:p-8">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="text-2xl font-extrabold text-foreground">{title}</h2>
        <span className="shrink-0 text-xs text-muted-foreground">
          {filtered.length} {filtered.length === 1 ? "page" : "pages"}
          {q && filtered.length !== links.length ? ` of ${links.length}` : ""}
        </span>
      </div>
      {description && (
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{description}</p>
      )}
      <ul className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((l) => (
          <li key={l.to}>
            <Link
              to={l.to}
              className="block rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground transition-smooth hover:border-brand-gold/60 hover:text-brand-gold-deep"
            >
              {l.label}
              <span className="ml-2 text-xs text-muted-foreground">{l.to}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

type SectionData = {
  title: string;
  description?: string;
  links: LinkRow[];
};

function SitemapSearch({ sections }: { sections: SectionData[] }) {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();

  const { totalAll, totalMatching } = useMemo(() => {
    const total = sections.reduce((acc, s) => acc + s.links.length, 0);
    const matching = q
      ? sections.reduce(
          (acc, s) =>
            acc +
            s.links.filter(
              (l) =>
                l.label.toLowerCase().includes(q) ||
                l.to.toLowerCase().includes(q),
            ).length,
          0,
        )
      : total;
    return { totalAll: total, totalMatching: matching };
  }, [sections, q]);

  return (
    <>
      <div className="mb-8">
        <label htmlFor="sitemap-search" className="sr-only">
          Filter pages
        </label>
        <div className="relative">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground"
          />
          <input
            id="sitemap-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by category, city, service, or URL — e.g. denver, seo, hvac, pricing"
            className="h-14 w-full rounded-xl border border-border bg-card pl-12 pr-12 text-base text-foreground shadow-card outline-none transition-smooth placeholder:text-muted-foreground focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/30"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear filter"
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-2 text-muted-foreground transition-smooth hover:bg-secondary hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="mt-2 px-1 text-xs text-muted-foreground" aria-live="polite">
          {q
            ? `Showing ${totalMatching} of ${totalAll} pages matching "${query}"`
            : `${totalAll} pages across ${sections.length} categories`}
        </div>
      </div>

      <div className="space-y-8">
        {sections.map((s) => (
          <Section key={s.title} {...s} query={query} />
        ))}
        {q && totalMatching === 0 && (
          <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
            <p className="text-base font-semibold text-foreground">
              No pages match "{query}"
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Try a different keyword like a city name (denver, boulder), a service
              (seo, web design, ppc), or an industry (hvac, plumbing).
            </p>
            <button
              type="button"
              onClick={() => setQuery("")}
              className="mt-4 inline-flex items-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground transition-smooth hover:border-brand-gold/60 hover:text-brand-gold-deep"
            >
              <X className="h-4 w-4" />
              Clear filter
            </button>
          </div>
        )}
      </div>
    </>
  );
}

function SitemapPage() {
  const core: LinkRow[] = [
    { label: "Home", to: "/" },
    { label: "About", to: "/about" },
    { label: "Meet Chris", to: "/meet-chris" },
    
    { label: "Case Studies", to: "/case-studies" },
    { label: "Reviews", to: "/reviews" },
    { label: "Contact", to: "/contact" },
    { label: "Are We a Fit?", to: "/are-we-a-fit" },
    { label: "Free Audit", to: "/free-audit" },
    
  ];

  const services: LinkRow[] = [
    { label: "All Services", to: "/digital-marketing" },
    { label: "Web Design", to: "/colorado-springs-web-design" },
    { label: "SEO", to: "/colorado-springs-seo" },
    { label: "Local SEO / Google Maps", to: "/colorado-springs-google-maps" },
    { label: "Google Ads / PPC", to: "/colorado-springs-ppc" },
    { label: "Digital Marketing", to: "/digital-marketing" },
    { label: "GEO & AI Search", to: "/geo-ai" },
  ];

  const pricing: LinkRow[] = [
    { label: "Pricing", to: "/pricing" },
  ];

  const cities: LinkRow[] = [
    { label: "All Service Areas", to: "/service-areas" },
    ...CITY_PAGES.flatMap((c) => [
      { label: `${c.name} — Hub`, to: `/service-areas/${c.slug}` },
      { label: `${c.name} SEO`, to: `/${c.slug}-seo` },
      { label: `${c.name} Web Design`, to: `/${c.slug}-web-design` },
    ]),
  ];

  const coloradoSpringsHub: LinkRow[] = [
    { label: "Colorado Springs Digital Marketing Agency (Hub)", to: "/digital-marketing" },
    { label: "Colorado Springs Google Maps", to: "/colorado-springs-google-maps" },
    { label: "Colorado Springs PPC", to: "/colorado-springs-ppc" },
    { label: "Colorado Springs GEO AI", to: "/colorado-springs-geo-ai" },
    { label: "Colorado Springs SEO", to: "/colorado-springs-seo" },
    { label: "Colorado Springs Web Design", to: "/colorado-springs-web-design" },
  ];

  const industries: LinkRow[] = [
    { label: "All Industries", to: "/industries" },
    ...INDUSTRY_PAGES.map((i) => ({
      label: i.name,
      to: `/industries/${i.slug}`,
    })),
  ];

  const industryHubs: LinkRow[] = [
    { label: "HVAC Marketing Colorado (Hub)", to: "/hvac-marketing-colorado" },
    { label: "HVAC SEO Colorado", to: "/hvac-seo-colorado" },
    { label: "HVAC Web Design Colorado", to: "/hvac-web-design-colorado" },
    { label: "HVAC Google Maps Colorado", to: "/hvac-google-maps-colorado" },
    { label: "Plumbing Marketing Colorado (Hub)", to: "/plumbing-marketing-colorado" },
    { label: "Plumbing SEO Colorado", to: "/plumbing-seo-colorado" },
    { label: "Plumbing Google Maps Colorado", to: "/plumbing-google-maps-colorado" },
    { label: "Plumbing Web Design Colorado", to: "/plumbing-web-design-colorado" },
    { label: "Roofing SEO Colorado (Hub)", to: "/roofing-seo-colorado" },
    { label: "Roofing Google Maps Colorado", to: "/roofing-google-maps-colorado" },
    { label: "Roofing PPC Colorado", to: "/roofing-ppc-colorado" },
    { label: "Roofing Web Design Colorado", to: "/roofing-web-design-colorado" },
    { label: "Electrical Contractor Marketing Colorado (Hub)", to: "/electrical-contractor-marketing-colorado" },
    { label: "Electrical SEO Colorado", to: "/electrical-seo-colorado" },
    { label: "Electrical Google Maps Colorado", to: "/electrical-google-maps-colorado" },
    { label: "Law Firm SEO Colorado (Hub)", to: "/law-firm-seo-colorado" },
    { label: "Law Firm Google Maps Colorado", to: "/law-firm-google-maps-colorado" },
    { label: "Law Firm PPC Colorado", to: "/law-firm-ppc-colorado" },
    { label: "Dental Marketing Colorado (Hub)", to: "/dental-marketing-colorado" },
    { label: "Dental SEO Colorado", to: "/dental-seo-colorado" },
    { label: "Dental Google Maps Colorado", to: "/dental-google-maps-colorado" },
    { label: "Landscaping SEO Colorado", to: "/landscaping-seo-colorado" },
  ];

  const intersectionPages: LinkRow[] = INTERSECTION_CONFIGS.map((c) => ({
    label: `${c.industry} ${c.service} in ${c.city}`,
    to: `/${c.slug}`,
  }));

  const honest: LinkRow[] = [
    { label: "Honest Answers", to: "/honest-answers" },
    { label: "When We're Not a Fit", to: "/honest-answers/not-a-fit" },
    { label: "Agency Red Flags", to: "/honest-answers/red-flags" },
    { label: "Real Website Cost", to: "/honest-answers/website-cost" },
  ];

  const learnHub: LinkRow[] = [
    { label: "Learning Hub", to: "/learn" },
    ...TOPIC_LANDINGS.map((t) => ({
      label: `Topic: ${t.h1}`,
      to: `/learn/topic/${t.slug}`,
    })),
  ];

  const articles: LinkRow[] = ARTICLES.map((a) => ({
    label: a.title,
    to: a.slug,
  }));

  const legal: LinkRow[] = [
    { label: "Privacy Policy", to: "/privacy" },
    { label: "Terms of Service", to: "/terms" },
    { label: "Accessibility", to: "/accessibility" },
    { label: "Editorial Policy", to: "/editorial-policy" },
  ];

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Site Map"
        title={
          <>
            Every page on{" "}
            <span className="text-gradient-gold">Colorado Web Impressions</span>
          </>
        }
        description="A human-friendly index of every major URL on the site, organized by category."
      />

      <section className="bg-background py-14 lg:py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          {/* Search */}
          <SitemapSearch
            sections={[
              { title: "Core & Company", description: "The pages most visitors land on first.", links: core },
              { title: "Services", description: "Six service hubs covering everything we do.", links: services },
              { title: "Pricing", description: "Transparent pricing for every service line.", links: pricing },
              { title: "City Pages", description: "Every Colorado city we serve, plus dedicated SEO and web design spokes.", links: cities },
              { title: "Colorado Springs Hub & Spokes", description: "Our anchor city — canonical hub plus dedicated service spokes for Google Maps, PPC, GEO AI, SEO, and Web Design.", links: coloradoSpringsHub },
              { title: "Industries", description: "Vertical-specific landing pages for the trades and professional services we work with.", links: industries },
              { title: "Industry Hubs & Spokes", description: "Dedicated industry marketing hubs and service spokes — HVAC, plumbing, roofing, electrical, law firms, dental, and more.", links: industryHubs },
              { title: "Tier 7 — Industry × City × Service", description: "Hyper-local intersection pages: each industry combined with our priority Colorado cities for SEO.", links: intersectionPages },
              { title: "Honest Answers", description: "Transparency hub — when we're not the right fit, agency red flags, and realistic costs.", links: honest },
              { title: "Learning Hub", description: "Guides, podcast, videos, and topic landing pages.", links: learnHub },
              { title: "Articles", description: "Every published article in the learning hub.", links: articles },
              { title: "Legal & Policies", links: legal },
            ]}
          />
        </div>
      </section>
    </SiteLayout>
  );
}
